// =============================================================================
// ROLE AXES — the shared, cross-user living taxonomy (AXIS-P1, docs/design/
// axis-lens-plan-2026-07-06.md §1, THE SYNTHESIS v2 §8). NO userId — mirrors
// industry_vocab.ts's own "researched ONCE and reused by everyone" shared-lake
// posture (industry_vocab.ts:1-14) and reuses its EXACT provenance/lifecycle
// shape (lakeProvenance from _shared.ts) per the red-team's own cheap
// mitigation ("reuse lakeProvenance/substrate, don't hand-roll a new status
// enum" — docs/research/axis-lens/red-team.md, "Read this first").
//
// WHAT THIS IS: a growable set of "what does this role ask of a person"
// dimensions (people-leadership, executive-communication, architecture, ...),
// at O*NET's Generalized-Work-Activity GRAIN (see docs/research/axis-lens/
// taxonomy-landscape.md §7) — NOT a fixed enum. Contrast with
// JobScoringService.ts's `radarAxes` (a candidate-FIT rubric, six hardcoded
// keys, scored by LLM judgment) — role_axes is a DIFFERENT, additive concept:
// a JD-WEIGHT read, cue-detected, growable. The two must never share a bare
// "axes" word in copy without qualifying which one (red-team kill #2).
//
// STATUS LIFECYCLE (P1 ships 'seed' rows only; 'derived'/'merged-into' are
// P4's growth-loop vocabulary, named here now so the column never needs a
// migration when P4 lands):
//   'seed'         — the ~10 O*NET-GWA-grounded starting rows (this file's
//                    seed SQL, docs/design/axis-lens-p1-neon.sql).
//   'derived'      — P4: auto-adopted from a JD's own text when coverage is
//                    thin. NOT built in P1 — no code path in this patch
//                    writes this status.
//   'merged-into'  — P4/merge-pass: a near-duplicate axis folded into another,
//                    NEVER deleted (mergedIntoAxisKey names the survivor).
//   'dormant'      — P4/merge-pass: unused, flagged, never dropped.
//
// ADDITIVE ONLY (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool
// (docs/design/axis-lens-p1-neon.sql — NOT applied by this lane; the
// orchestrator applies it), mirrored here in Drizzle and exported from
// index.ts.
//
// ATTRIBUTION (CC BY 4.0 — docs/research/axis-lens/taxonomy-landscape.md §1):
// several seed rows' detectionCues are grounded in the U.S. Department of
// Labor's O*NET Generalized Work Activities. Required verbatim notice:
// "This page includes information from the O*NET 30.3 Database by the U.S.
// Department of Labor, Employment and Training Administration (USDOL/ETA).
// Used under the CC BY 4.0 license." Our cue lists are DERIVED/reshaped from
// GWA concepts, not verbatim O*NET text, so per the license's modification
// clause: USDOL/ETA has not approved, endorsed, or tested this derivation.
// See docs/design/axis-lens-p1-neon.sql's header for the SQL-side copy of
// this same notice (the DB row data carries the same obligation as the code).
// =============================================================================

import { sql } from 'drizzle-orm';
import { index, integer, jsonb, pgTable, text, varchar } from 'drizzle-orm/pg-core';
import { id, lakeProvenance, timestamps } from './_shared';

/** One axis's row shape. `axisKey` is the stable slug every consumer (the
 *  lens read, the affinity tagger in P2, the dot product in P3) keys on —
 *  never the display `name`, which may be edited without breaking a join. */
export const roleAxes = pgTable(
  'role_axes',
  {
    id: id(),
    /** Dedup key — slug, e.g. 'executive-communication'. UNIQUE = the same
     *  cheap admission-control discipline industry_vocab.roleFamilyKey
     *  already proves (red-team S1's named mitigation): a derived axis (P4)
     *  cannot silently mint a near-duplicate row under a new key without at
     *  least colliding here first. */
    axisKey: varchar('axis_key', { length: 80 }).notNull().unique(),
    /** Display name, e.g. 'Executive Communication'. */
    name: varchar('name', { length: 160 }).notNull(),
    /** What this axis represents — one or two sentences, shown wherever the
     *  axis itself needs explaining (a future admin/merge-review surface). */
    meaning: text('meaning').notNull(),
    /** The cue phrases the (P1) coverage pass / (P2+) association pass match
     *  against JD and achievement text. string[] — plain data, growable
     *  without a migration (mirrors industry_vocab.keywords' own jsonb-array
     *  choice for an open-ended set, corpus-proving-ground.md §1.6). */
    detectionCues: jsonb('detection_cues').$type<string[]>().notNull().default(sql`'[]'::jsonb`),
    /** 'seed' | 'derived' | 'merged-into' | 'dormant'. varchar, not a DB enum
     *  — this codebase's house convention (achievements.status,
     *  achievementAlignmentRulings.ruling all take a plain varchar so a new
     *  value is a data event, never a migration). */
    status: varchar('status', { length: 16 }).notNull().default('seed'),
    /** Set only when status === 'merged-into' — the surviving axisKey. */
    mergedIntoAxisKey: varchar('merged_into_axis_key', { length: 80 }),
    /** Provenance: null for a seed row; for a P4-derived row, the JD/source
     *  that spawned it (job id, excerpt, timestamp) — never invented, always
     *  traceable to real posting text (NO INVENTION, plan §3). Unused by any
     *  P1 code path (P1 ships seed rows only) but the column exists now so
     *  P4 never needs a migration to start writing it. */
    createdFrom: jsonb('created_from'),
    /** Coverage/hit-count telemetry — additive room to grow (dormancy
     *  tracking, merge-pass co-firing stats). Unused by P1's read path
     *  (P1 does not write usage stats — that's a P4/P7 concern) but reserved
     *  now per the same no-migration-later discipline as createdFrom. */
    usageStats: jsonb('usage_stats'),
    /** Bumped on a taxonomy-affecting change (a new axis added, cues
     *  rewritten). Every listing_axis_reads row stamps the taxonomyVersion
     *  it was read against, so a later bump never silently reinterprets an
     *  old read. */
    taxonomyVersion: integer('taxonomy_version').notNull().default(1),
    ...lakeProvenance,
    ...timestamps,
  },
  (t) => ({
    statusIdx: index('role_axes_status_idx').on(t.status),
  }),
);
